import { mapGetters } from 'vuex';
import { getStateOfParamKey } from '@/store/modules/dataInputMenu/getters';
import { buildListenersObject } from '../../../../../utils/helpers';
import {
	CLOSE_MENU,
	REFRESH_DATA,
	SET_VALUE_FROM_EXTERNAL_SOURCE,
	SET_VALUE_FROM_PREVIOUS_STEP,
	SET_TEXT_VALUE,
	SET_VALUE_FROM_TABLE_SOURCE,
	UPDATE_SEARCH_STRING,
	RE_CLAC_MENU_POSITION,
} from '../../../../DataInput/DataInputMenu/helpers/data-input-menu.const';
import DataInputMenuAbsoluteMenuWrapper from '@/components/common/DataInputMenuAbsoluteMenuWrapper';
import IntersectionObserverMixin from './IntersectionObserverMixin';
import ScrollResizeListenersMixin from './ScrollResizeListenersMixin';
import DataInputMenuService from '@/components/DataInput/DataInputMenu/data-input-menu-service';

export default {
	components: { DataInputMenuAbsoluteMenuWrapper },
	mixins: [IntersectionObserverMixin, ScrollResizeListenersMixin],
	props: {
		param: { type: Object }, // the only prop that is not reactive, we get it in the "normal" way so we can get the rest of the relevant props
	},
	computed: {
		...mapGetters([getStateOfParamKey.fullPath]),
		dataInputMenuProps() {
			return this[getStateOfParamKey.fullPath](this.param.dataInputKey || this.param.key);
		},
		inputMenuListeners() {
			const listenersFunctions = [
				SET_VALUE_FROM_PREVIOUS_STEP,
				SET_TEXT_VALUE,
				SET_VALUE_FROM_EXTERNAL_SOURCE,
				REFRESH_DATA,
				CLOSE_MENU,
				SET_VALUE_FROM_TABLE_SOURCE,
				UPDATE_SEARCH_STRING,
				RE_CLAC_MENU_POSITION,
			];
			return buildListenersObject(this, listenersFunctions);
		},
		localTab: {
			set(value) {
				this.$emit('update:tab', value);
			},
			get() {
				return this.tab;
			},
		},
		allowMultipleSelection() {
			return this.dataInputMenuProps.allowMultipleSelection;
		},
		tabItems() {
			return this.dataInputMenuProps.tabItems;
		},
		tab() {
			return this.dataInputMenuProps.tab;
		},
		localValue() {
			return this.dataInputMenuProps.localValue;
		},
		absoluteMenuProps() {
			return {
				positionX: this.$data._positionX,
				positionY: this.$data._positionY,
				[this.$data._menuDirection]: true,
				'close-on-content-click': false,
				'close-on-click': false,
			};
		},
		isBottomToTopLayout() {
			return this.$data._isBottomToTopLayout;
		},
		didOpenBySlash() {
			return this.$data._didOpenBySlash;
		},
	},
	methods: {
		reCalcMenuPosition() {
			this._prepareAbsoluteMenuProps();
		},
		_handleIntersectionEntries(entries) {
			entries.forEach(this._handleIntersectionEntry);
		},
		_handleIntersectionEntry(entry) {
			// observer is observing one element at a time, so if it's not intersecting with it, we should hide menu
			if (!entry.isIntersecting) {
				this.hideMenu();
			}
		},
		// args are : targetElement, dataInputMenuElement, didOpenBySlash, menuWidth , menuHeight, carretCoordinatesOnSlashOpen,
		/**
		 * onScroll handler
		 * @param {*} args
		 */
		_onScroll(args) {
			this._prepareAbsoluteMenuProps(args);
		},
		/**
		 * onResize handler
		 * @param {*} args
		 */
		_onResize(args) {
			this._prepareAbsoluteMenuProps(args);
		},
		_listenToEvents(args) {
			// sign for scroll and resize events - managed in ScrollResizeListenersMixin
			!args.didOpenBySlash && this._listenOnScroll(args, this._onScroll); //TODO: make listen on scroll to avoid scroll inside the dataInputMenu
			this._listenOnResize(args, this._onResize);
		},
		_removeEventListeners() {
			this._removeOnScrollListener();
			this._removeOnResizeListener();
		},
		showMenu(args) {
			let { targetElement } = args || {};

			if (!targetElement) {
				targetElement = this.$el;
			}

			// IntersectionObserver logic - managed in IntersectionObserverMixin
			this._createIntersectionObserver(this._handleIntersectionEntries);
			this.$data._intersectionObserver.observe(targetElement);

			this._listenToEvents({ ...args, targetElement });
			this._prepareAbsoluteMenuProps({ ...args, targetElement });
			this.showMenuFlag = true;
		},
		hideMenu() {
			// IntersectionObserver logic - managed in IntersectionObserverMixin
			this._removeIntersectionObserver();

			this._removeEventListeners();

			this.showMenuFlag = false;
			this.$data._didOpenBySlash = false;
		},
		_calcRect(args) {
			const menuRect = DataInputMenuService.getMenuRect(args);
			this.$data._menuDirection = menuRect.dir;
			this.$data._positionX = menuRect.x;
			this.$data._positionY = menuRect.y;
			this.$data._isBottomToTopLayout = menuRect.isBottomToTopLayout;
			this.$data._didOpenBySlash = menuRect.didOpenBySlash;
		},
		_prepareAbsoluteMenuProps(args = this.lastUsedArgsToCalcMenuPosition) {
			const dataInputMenuElement = this.$refs.data_input_menu?.$el;
			this._calcRect({ ...args, dataInputMenuElement });
			this.lastUsedArgsToCalcMenuPosition = { ...args, dataInputMenuElement };
		},
	},
	data() {
		return {
			showMenuFlag: false,
			_positionX: null,
			_positionY: null,
			_menuDirection: 'bottom',
			_isBottomToTopLayout: false,
			_didOpenBySlash: false,
			lastUsedArgsToCalcMenuPosition: {},
		};
	},
	watch: {
		showMenuFlag: {
			handler(newV) {
				if (!newV) {
					this.$data._didOpenBySlash = false;
					this.lastUsedArgsToCalcMenuPosition = { ...this.lastUsedArgsToCalcMenuPosition, didOpenBySlash: false };
					this.$emit(CLOSE_MENU);
				}
			},
		},
	},
};
